import React from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';
import { BillItem } from '../../../pages/hospital/Billing/types';
import { typographyStyles } from '../styles/typography';
import { createT } from '../utils/simpleTranslate';
import { formatDateForPDF } from '../utils/dataFormatters';
import { INSURANCE_BILL_COLUMNS, INSURANCE_BILL_PDF_COLUMNS, PDFColumnConfig } from '../../../pages/hospital/Billing/insuranceBillColumns';

/**
 * PDF Insurance Bill Items Table Component
 * 
 * Dynamically renders table based on shared column configuration.
 * Changes to INSURANCE_BILL_COLUMNS in insuranceBillColumns.ts will automatically
 * reflect in both InsuranceBillView and this PDF table.
 */

interface PDFInsuranceBillItemsTableProps {
  items: (BillItem & {
    patientName?: string;
    patientPaymentAmount?: number;
    patientBillCreatedAt?: Date;
    patientBillId?: string;
    souscripteur?: string;
  })[];
  currency: string;
  locale?: string;
}

const formatCurrency = (amount: number, currency: string, locale: string): string => {
  const formatted = new Intl.NumberFormat(locale).format(amount).replace(/[\u00A0\u202F\u2009]/g, ' ');
  return `${formatted} ${currency}`;
};

export const PDFInsuranceBillItemsTable: React.FC<PDFInsuranceBillItemsTableProps> = ({
  items,
  currency,
  locale = 'fr',
}) => {
  const t = createT(locale);

  // Verify that PDF columns match the shared column configuration
  const columnNames = INSURANCE_BILL_COLUMNS.map(col => col.name as string);
  const pdfColumnNames = INSURANCE_BILL_PDF_COLUMNS.map(col => col.name);
  
  if (columnNames.length !== pdfColumnNames.length || 
      !columnNames.every((name, idx) => name === pdfColumnNames[idx])) {
    console.warn('[PDFInsuranceBillItemsTable] Column mismatch between INSURANCE_BILL_COLUMNS and INSURANCE_BILL_PDF_COLUMNS');
  }

  // Create dynamic styles for columns based on configuration
  const createColumnStyle = (config: PDFColumnConfig) => ({
    width: config.width,
    textAlign: config.textAlign || 'left' as const,
  });

  const styles = StyleSheet.create({
    table: {
      width: '100%',
      marginTop: 10,
      marginBottom: 0,
    },
    headerRow: {
      flexDirection: 'row',
      borderBottomWidth: 1,
      borderBottomColor: '#000',
      paddingBottom: 4,
      marginBottom: 4,
      backgroundColor: '#f5f5f5',
      padding: 4,
    },
    row: {
      flexDirection: 'row',
      paddingVertical: 3,
      borderBottomWidth: 0.5,
      borderBottomColor: '#E0E0E0',
      padding: 4,
    },
    headerText: {
      ...typographyStyles.body2,
      fontWeight: 'bold',
      fontSize: 8,
      flexWrap: 'wrap',
    },
    cellText: {
      ...typographyStyles.body2,
      fontSize: 8,
    },
  });

  // Helper to get numeric amount value for a column (replicates BillItems logic)
  // This matches the logic in BillItems.tsx lines 515-529
  const getAmountValue = (item: any, columnName: string): number => {
    if (columnName === 'patientPaymentAmount') {
      // Calculate from insuranceClaim if available, otherwise use stored value
      if (item.insuranceClaim) {
        return item.insuranceClaim.patientTotalBill - item.insuranceClaim.approvedAmount;
      }
      return item.patientPaymentAmount || 0;
    } else if (columnName === 'total') {
      // Get total from insuranceClaim
      return item.insuranceClaim?.patientTotalBill || 0;
    } else if (columnName === 'amount') {
      // Use standard amount field (for insurance bills, this should be approvedAmount)
      const amount = typeof item.amount === 'string' ? parseFloat(item.amount) : item.amount;
      return amount || 0;
    }
    return 0;
  };

  // Helper to get cell value based on column name
  const getCellValue = (item: any, columnName: string): string => {
    switch (columnName) {
      case 'concept':
        return item.concept || '-';
      case 'patientName':
        return item.patientName || '-';
      case 'patientBillId':
        return item.patientBillId || '-';
      case 'souscripteur':
        return item.souscripteur || '-';
      case 'amount':
      case 'total':
      case 'patientPaymentAmount': {
        const amountValue = getAmountValue(item, columnName);
        return formatCurrency(amountValue, currency, locale);
      }
      case 'patientBillCreatedAt':
        return item.patientBillCreatedAt 
          ? formatDateForPDF(item.patientBillCreatedAt, locale)
          : '-';
      case 'quantity':
        return String(item.quantity || 1);
      default:
        return '-';
    }
  };

  // Calculate totals for columns that need them
  // Reuses the same getAmountValue logic for consistency
  const calculateTotal = (columnName: string): number => {
    return items.reduce((sum, item) => {
      return sum + getAmountValue(item, columnName);
    }, 0);
  };

  return (
    <View style={styles.table}>
      {/* Table Header */}
      <View style={styles.headerRow} wrap={false} minPresenceAhead={20}>
        {INSURANCE_BILL_PDF_COLUMNS.map((colConfig, index) => (
          <Text
            key={colConfig.name}
            style={[styles.headerText, createColumnStyle(colConfig)]}
          >
            {t(colConfig.translationKey)}
          </Text>
        ))}
      </View>

      {/* Table Body */}
      {items.map((item, itemIndex) => (
        <View key={item.id || itemIndex} style={styles.row} wrap={false}>
          {INSURANCE_BILL_PDF_COLUMNS.map((colConfig) => (
            <Text 
              key={colConfig.name}
              style={[styles.cellText, createColumnStyle(colConfig)]}
            >
              {getCellValue(item, colConfig.name)}
            </Text>
          ))}
        </View>
      ))}

      {/* Total Row */}
      {items.length > 0 && (
        <View wrap={false} style={[styles.row, {
          borderTopWidth: 1,
          borderTopColor: '#000',
          marginTop: 4,
          backgroundColor: '#f9f9f9',
          fontWeight: 'bold'
        }]}>
          {INSURANCE_BILL_PDF_COLUMNS.map((colConfig) => {
            if (colConfig.name === 'concept') {
              return (
                <Text 
                  key={colConfig.name}
                  style={[styles.cellText, createColumnStyle(colConfig), { fontWeight: 'bold' }]}
                >
                  {t('hospital.billing.bill.total')}
                </Text>
              );
            } else if (colConfig.isTotalColumn) {
              const total = calculateTotal(colConfig.name);
              return (
                <Text 
                  key={colConfig.name}
                  style={[styles.cellText, createColumnStyle(colConfig), { fontWeight: 'bold' }]}
                >
                  {formatCurrency(total, currency, locale)}
                </Text>
              );
            } else {
              return (
                <Text 
                  key={colConfig.name}
                  style={[styles.cellText, createColumnStyle(colConfig)]}
                >
                  {' '}
                </Text>
              );
            }
          })}
        </View>
      )}
    </View>
  );
};

export default PDFInsuranceBillItemsTable;

